import { create } from "zustand"
import { devtools } from "zustand/middleware"
import type { WidgetConfig, WidgetStatus } from "../types/widget"
import { DEFAULT_WIDGET_CONFIG } from "../constants/widget-config"
import { getWidgetConfig } from "../services/widget"        

interface WidgetStore {
  // Store State
  isLoading: boolean
  error: string | null
  activeDevice: keyof WidgetConfig["position"]
  config: WidgetConfig

  // API Actions
  fetchConfig: () => Promise<void>

  // Icon Actions
  setIconId: (iconId: string) => void
  setIconSize: (size: number) => void
  setPrimaryColor: (color: string) => void
  setLabel: (label: string) => void
  setHeading: (heading: string) => void
  setStatus: (status: WidgetStatus) => void
  setStatementEnabled: (enabled: boolean) => void
  setStatementUrl: (url: string) => void
  setLanguage: (language: string) => void
  // Position Actions
  setPosition: (device: keyof WidgetConfig["position"], position: string) => void
  setMargin: (device: keyof WidgetConfig["margins"], side: keyof WidgetConfig["margins"]["desktop"], value: number) => void
  setActiveDevice: (device: keyof WidgetConfig["position"]) => void

  // Utility Actions
  resetToDefaults: () => void
  loadConfig: (config: Partial<WidgetConfig>) => void
}

export const useWidgetStore = create<WidgetStore>()(
  devtools(
    (set) => ({
      isLoading: true,
      error: null,
      activeDevice: "desktop",
      config: DEFAULT_WIDGET_CONFIG,

      // API Actions
      fetchConfig: async () => {
        set({ isLoading: true, error: null })
        try {
          const response = await getWidgetConfig()
          set({ config: response as unknown as WidgetConfig })
        } catch (error) {
          console.error(error);
          set({ error: "An unexpected error occurred" })
        } finally {
          set({ isLoading: false })
        }
      },

      // Icon Actions
      setIconId: (iconId: string) => 
        set((state) => ({ config: { ...state.config, iconId } }), false, "setIconId"),
      setIconSize: (iconSize: number) => 
        set((state) => ({ config: { ...state.config, iconSize } }), false, "setIconSize"),
      setPrimaryColor: (primaryColor: string) => 
        set((state) => ({ config: { ...state.config, primaryColor } }), false, "setPrimaryColor"),
      setLabel: (label: string) => 
        set((state) => ({ config: { ...state.config, label } }), false, "setLabel"),
      setHeading: (heading: string) => 
        set((state) => ({ config: { ...state.config, heading } }), false, "setHeading"),
      setStatus: (status: WidgetStatus) => 
        set((state) => ({ config: { ...state.config, status } }), false, "setStatus"),
      setStatementEnabled: (enabled: boolean) => 
        set((state) => ({ config: { ...state.config, modules: { ...state.config.modules, statement: { ...state.config.modules.statement, enabled } } } }), false, "setStatementEnabled"),
      setStatementUrl: (url: string) => 
        set((state) => ({ config: { ...state.config, modules: { ...state.config.modules, statement: { ...state.config.modules.statement, url } } } }), false, "setStatementUrl"),
      setLanguage: (language: string) =>
        set((state) => ({ config: { ...state.config, language: { ...state.config.language, default: language } } }), false, "setLanguage"),
      // Position Actions
      setPosition: (device, position) =>
        set(
          (state) => ({
            config: {
              ...state.config,
              position: {
                ...state.config.position,
                [device]: position,
              },
            },
          }),
          false,
          "setPosition",
        ),

      setMargin: (device, side, value) =>
        set(
          (state) => ({
            config: {
              ...state.config,
              margins: {
                ...state.config.margins,
                [device]: {
                  ...state.config.margins[device],
                  [side]: value,
                },
              },
            },
          }),
          false,
          "setMargin",
        ),

      setActiveDevice: (device) =>
        set(
          { activeDevice: device },
          false,
          "setActiveDevice",
        ),

      // Utility Actions
      resetToDefaults: () => set({ config: DEFAULT_WIDGET_CONFIG }, false, "resetToDefaults"),

      loadConfig: (newConfig: Partial<WidgetConfig>) => {
        set(
          (state) => ({
            config: {
              ...state.config,
              ...newConfig,
              position: newConfig.position ? { ...state.config.position, ...newConfig.position } : state.config.position,
              margins: newConfig.margins ? { ...state.config.margins, ...newConfig.margins } : state.config.margins,
              modules: {
                color: {
                  ...state.config.modules.color,
                  ...(newConfig.modules?.color || {}),
                },
                content: {
                  ...state.config.modules.content,
                  ...(newConfig.modules?.content || {}),
                },
                statement: {
                  ...state.config.modules.statement,
                  ...(newConfig.modules?.statement || {}),
                },
              },
            },
          }),
          false,
          "loadConfig",
        )
      },
    }),
    { name: "widget-store" },
  ),
)

// Legacy hook for backward compatibility
export const useWidgetSettings = () => {
  const store = useWidgetStore()

  return {
    settings: {
      selectedIcon: store.config.iconId,
      iconSize: [store.config.iconSize],
      primaryColor: store.config.primaryColor,
      position: store.config.position,
      margins: store.config.margins,
      statement: store.config.modules.statement,
    },
    setters: {
      setSelectedIcon: store.setIconId,
      setIconSize: (size: number[]) => store.setIconSize(size[0]),
      setPrimaryColor: store.setPrimaryColor,
      setLineHeight: () => {},
      setDyslexicFont: () => {},
      setTextAlignment: () => {},
      setBrightness: () => {},
      setContrast: () => {},
      setSaturation: () => {},
    },
    handlers: {
      handlePositionChange: (device: string, value: string) =>
        store.setPosition(device as keyof WidgetConfig["position"], value),
      handleMarginChange: (device: string, side: string, value: number) =>
        store.setMargin(
          device as keyof WidgetConfig["margins"],
          side as keyof WidgetConfig["margins"]["desktop"],
          value
        ),
      resetToDefaults: store.resetToDefaults,
    },
  }
}
